import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Info, AlertCircle, X, Copy, Check } from 'lucide-react';

/**
 * Toast — notification globale 4 niveaux (success / error / info / warning).
 *
 * Mounted dans Layout.js. Ecoute event 'erp:notification' :
 *   window.dispatchEvent(new CustomEvent('erp:notification', { detail: {
 *     level: 'success' | 'error' | 'info' | 'warning',
 *     message: 'Texte',
 *     code: 'E-XXXXXX',     // optionnel pour error
 *     duration: 2500        // optionnel ms
 *   }}))
 *
 * Helpers : utils/notifications.js
 */

const LEVELS = {
  success: {
    bg: 'bg-emerald-900',
    border: 'border-emerald-700',
    iconColor: 'text-emerald-300',
    Icon: CheckCircle,
    duration: 2500,
  },
  error: {
    bg: 'bg-red-900',
    border: 'border-red-700',
    iconColor: 'text-red-300',
    Icon: AlertTriangle,
    duration: 5000,
  },
  warning: {
    bg: 'bg-amber-900',
    border: 'border-amber-700',
    iconColor: 'text-amber-300',
    Icon: AlertCircle,
    duration: 4000,
  },
  info: {
    bg: 'bg-blue-900',
    border: 'border-blue-700',
    iconColor: 'text-blue-300',
    Icon: Info,
    duration: 3000,
  },
};

const Toast = () => {
  const [toasts, setToasts] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const addToast = useCallback((e) => {
    const { level = 'info', message, code, duration } = e.detail || {};
    const config = LEVELS[level] || LEVELS.info;
    const id = Date.now() + Math.random();
    const finalDuration = duration || config.duration;

    setToasts(prev => [...prev.slice(-4), { id, level, message, code, ts: new Date() }]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, finalDuration);
  }, []);

  useEffect(() => {
    window.addEventListener('erp:notification', addToast);
    return () => window.removeEventListener('erp:notification', addToast);
  }, [addToast]);

  const dismiss = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const copyCode = (id, code) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map(t => {
        const config = LEVELS[t.level] || LEVELS.info;
        const Icon = config.Icon;
        return (
          <div
            key={t.id}
            className={`${config.bg} text-white rounded-lg shadow-xl border ${config.border} p-4 flex flex-col gap-2`}
            role={t.level === 'error' ? 'alert' : 'status'}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Icon size={16} className={`${config.iconColor} flex-shrink-0 mt-0.5`} />
                <span className="text-sm font-medium">{t.message}</span>
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className={`${config.iconColor} hover:text-white flex-shrink-0`}
                aria-label="Fermer"
              >
                <X size={14} />
              </button>
            </div>

            {t.code && t.level === 'error' && (
              <div className="flex items-center gap-2 bg-red-950 rounded px-3 py-1.5">
                <span className="font-mono text-xs text-red-200 font-bold tracking-wider flex-1">
                  {t.code}
                </span>
                <button
                  onClick={() => copyCode(t.id, t.code)}
                  className="text-red-300 hover:text-white"
                  title="Copier"
                >
                  {copiedId === t.id
                    ? <Check size={13} className="text-green-400" />
                    : <Copy size={13} />
                  }
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Toast;
